import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GmailCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    // Pages set `sessionStorage.gmail_return_to` before kicking off OAuth
    // so we can send the user back exactly where they came from.
    const returnTo = sessionStorage.getItem('gmail_return_to') || '/estimates';
    sessionStorage.removeItem('gmail_return_to');

    if (!code || !state) {
      toast.error('Google authorization failed');
      navigate('/dashboard', { replace: true });
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API}/gmail/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ code, state }),
        });
        if (res.ok) {
          // Check which scopes landed so we can give a more specific toast.
          try {
            const st = await (await fetch(`${API}/gmail/status`, { credentials: 'include' })).json();
            if (st.has_calendar) {
              toast.success('Google connected — Gmail + Calendar ready!');
            } else {
              toast.success('Gmail connected successfully!');
            }
          } catch { toast.success('Google connected successfully!'); }
        } else {
          // Pull as much detail as we can out of the response. Some proxies
          // / service-workers read the body before us, which used to crash
          // the whole callback with "body stream already read". Isolate
          // the body-read in its own try/catch so we always at least show
          // the HTTP status.
          let raw = '';
          try { raw = await res.clone().text(); }
          catch { try { raw = await res.text(); } catch { /* ignore */ } }

          let detail = '';
          if (raw) {
            try {
              const err = JSON.parse(raw);
              detail = err.detail || err.error || err.message || '';
            } catch { detail = raw; }
          }
          const surface = detail || `HTTP ${res.status} ${res.statusText || ''}`.trim();
          const preview = surface.length > 200 ? surface.slice(0, 200) + '…' : surface;
          try {
            window.localStorage.setItem('last_gmail_oauth_error', JSON.stringify({
              status: res.status, body: (raw || '').slice(0, 2000), at: new Date().toISOString(),
            }));
          } catch { /* storage quota */ }
          toast.error(`Google connect failed (${res.status}): ${preview}`, {
            duration: 20000,
            action: {
              label: 'Copy',
              onClick: () => {
                try { navigator.clipboard.writeText(`HTTP ${res.status}\n${raw}`); } catch { /* ignore */ }
              },
            },
          });
        }
      } catch (err) {
        toast.error(`Google connect failed: ${err.message || 'network error'}`, { duration: 20000 });
      }
      navigate(returnTo, { replace: true });
    })();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F6F5]">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-[#6B7A73] text-sm font-medium">Connecting Google…</p>
      </div>
    </div>
  );
}
